import React from 'react';
import { Facebook, Instagram, Youtube, Twitter } from 'lucide-react';

// Inline Pinterest icon (lucide has Pin but brand mark is clearer with SVG)
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.427 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345c-.091.379-.293 1.193-.333 1.36-.053.22-.174.267-.402.161-1.499-.698-2.436-2.888-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.608 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0" />
  </svg>
);

const socialLinks = [
  { Icon: Facebook, label: 'Facebook', href: 'https://www.facebook.com/AmericanPoolsSpas' },
  { Icon: Instagram, label: 'Instagram', href: 'https://www.instagram.com/americanpoolsspas/' },
  { Icon: Youtube, label: 'YouTube', href: 'https://www.youtube.com/channel/UC29-XIIPCYvfEz-ZJvhlcag' },
  { Icon: Twitter, label: 'Twitter', href: 'https://twitter.com/AmPoolsSpas' },
  { Icon: PinterestIcon, label: 'Pinterest', href: 'https://www.pinterest.com/Am_Pools_Spas/' },
];

const Footer = () => {
  return (
    <footer className="bg-white">
      {/* Main footer content */}
      <div className="max-w-[1400px] mx-auto px-5 pt-14 pb-10 grid grid-cols-1 md:grid-cols-3 gap-12">
        {/* Column 1 */}
        <div className="text-[#0b1e4d] text-[14px] leading-[1.9]">
          <h4 className="text-[15px] font-bold tracking-[0.08em] uppercase mb-4">
            American Pools &amp; Spas
          </h4>
          <p>7320 Narcoossee Road</p>
          <p>Orlando, FL 32822</p>
          <p>
            Phone:{' '}
            <a href="tel:4076066375" className="text-[#1e4fa3] hover:underline">
              407-606-6375
            </a>
          </p>
          <p className="mt-4 font-bold">Business Hours:</p>
          <p>8:00am to 5:00pm Monday-Friday</p>
          <p>10:00am to 4:00pm Saturday</p>
          <p className="mt-5">
            <a
              href="https://american-pools.com/customer-portal/"
              className="text-[#1e4fa3] hover:underline"
            >
              American Pools Customer Portal
            </a>
          </p>
        </div>

        {/* Column 2 */}
        <div className="text-[#0b1e4d] text-[14px] leading-[1.9]">
          <h4 className="text-[15px] font-bold tracking-[0.08em] uppercase mb-4">
            Inground Swimming Pools Orlando
          </h4>
          <p>
            We design and build inground swimming pools &amp; custom spas,
            outdoor kitchens, screen enclosures &amp; patios, pavers in Orlando
            and other Central Florida communities in Brevard County, Lake
            County, Orange County, Osceola County, Polk County, Seminole
            County, and Volusia County.
          </p>
          <p className="mt-3">
            We also design and build commercial pools in Florida.
          </p>
          <p className="mt-5">
            <a
              href="https://american-pools.com/privacy-policy/"
              className="text-[#1e4fa3] hover:underline"
            >
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Column 3 */}
        <div className="text-[#0b1e4d] text-[14px]">
          <h4 className="text-[15px] font-bold tracking-[0.08em] uppercase mb-4">
            Pool Builder Orlando
          </h4>

          {/* Social icons row */}
          <div className="flex items-center gap-3 mb-6">
            {socialLinks.map(({ Icon, label, href }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noreferrer"
                aria-label={label}
                className="w-10 h-10 rounded-full bg-[#1e4fa3] text-white flex items-center justify-center hover:bg-[#183d80] transition-colors"
              >
                <Icon className="w-[18px] h-[18px]" />
              </a>
            ))}
          </div>

          {/* BBB + Torch badges horizontal */}
          <div className="flex items-stretch gap-3 flex-wrap">
            {/* BBB */}
            <div className="flex items-stretch border border-gray-300 rounded bg-white overflow-hidden">
              <div className="bg-[#0a4b8f] text-white px-3 py-2 flex flex-col items-center justify-center">
                <span className="text-[11px] font-black leading-none">BBB</span>
                <span className="text-[7px] font-semibold leading-[1.1] mt-0.5 text-center">
                  ACCREDITED<br />BUSINESS
                </span>
              </div>
              <div className="px-3 py-2 text-[11px] leading-tight flex flex-col justify-center">
                <p className="font-bold">BBB Rating: A+</p>
                <p className="text-gray-500 text-[10px]">As of 2/28/2026</p>
                <a href="#" className="text-[#1e4fa3] text-[10px] hover:underline">
                  Click for Profile
                </a>
              </div>
            </div>

            {/* Torch Awards */}
            <div className="flex items-stretch border border-gray-300 rounded bg-white overflow-hidden">
              <div className="bg-[#0a4b8f] text-white px-3 py-2 flex flex-col items-center justify-center">
                <span className="text-[10px] font-black leading-none">Torch</span>
                <span className="text-[10px] font-black leading-none mt-0.5">AWARDS</span>
                <span className="text-[7px] italic leading-none mt-0.5">for Ethics</span>
              </div>
              <div className="px-4 py-2 text-[12px] font-bold text-[#0b1e4d] flex items-center">
                WINNER
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Partner companies row (matching live site logo row) */}
      <div className="bg-[#f3f6fb] border-t border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-5 py-7 grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
          {/* American Pools */}
          <a href="https://american-pools.com" className="flex flex-col items-center text-center hover:opacity-80 transition-opacity">
            <span className="text-[#0b1e4d] font-serif font-bold text-[22px] tracking-[0.08em] leading-none">
              AMERICAN
            </span>
            <span className="text-[#0b1e4d] text-[10px] font-bold tracking-[0.18em] mt-0.5">
              POOLS &amp; SPAS
            </span>
            <span className="text-[#0b1e4d] text-[10px] italic mt-1">
              A Cody Pools Company
            </span>
          </a>
          {/* California Pools */}
          <a href="https://californiapools.com" className="flex flex-col items-center text-center hover:opacity-80 transition-opacity">
            <span className="text-[#0b1e4d] font-serif font-bold text-[22px] tracking-[0.08em] leading-none">
              CALIFORNIA
            </span>
            <span className="text-[#0b1e4d] text-[10px] font-bold tracking-[0.18em] mt-0.5">
              POOLS &amp; LANDSCAPE
            </span>
            <span className="text-[#0b1e4d] text-[10px] italic mt-1">
              A Cody Pools Company
            </span>
          </a>
          {/* Cody Pools */}
          <a href="https://codypools.com" className="flex flex-col items-center text-center hover:opacity-80 transition-opacity">
            <span className="text-[#1e4fa3] font-serif font-black text-[26px] tracking-[0.04em] leading-none">
              CODY POOLS
            </span>
          </a>
          {/* Platinum Pools */}
          <a href="https://platinumpools.com" className="flex flex-col items-center text-center hover:opacity-80 transition-opacity">
            <span className="text-[#0b1e4d] font-serif font-bold text-[22px] tracking-[0.08em] leading-none">
              PLATINUM
            </span>
            <span className="text-[#0b1e4d] text-[10px] font-bold tracking-[0.18em] mt-0.5">
              POOLS
            </span>
            <span className="text-[#0b1e4d] text-[10px] italic mt-1">
              A Cody Pools Company
            </span>
          </a>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-white">
        <div className="max-w-[1400px] mx-auto px-5 py-7 text-center text-[12px] text-[#0b1e4d]">
          <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mb-4 uppercase font-semibold tracking-wider">
            <a href="/" className="hover:text-[#1e4fa3]">Home</a>
            <span className="text-gray-400">|</span>
            <a href="https://american-pools.com/blog/" className="hover:text-[#1e4fa3]">Blog</a>
            <span className="text-gray-400">|</span>
            <a href="https://american-pools.com/privacy-policy/" className="hover:text-[#1e4fa3]">Privacy Policy</a>
            <span className="text-gray-400">|</span>
            <a href="https://american-pools.com/website-accessibility-statement/" className="hover:text-[#1e4fa3]">Website Accessibility Statement</a>
            <span className="text-gray-400">|</span>
            <a href="https://american-pools.com/contact-us/" className="hover:text-[#1e4fa3]">Contact Us</a>
            <span className="text-gray-400">|</span>
            <a href="https://american-pools.com/request-a-quote/" className="hover:text-[#1e4fa3]">Request a Quote</a>
          </div>
          <p className="max-w-4xl mx-auto leading-relaxed">
            We are part of the Cody Pools group of companies as of 5/9/23. Ranked the #1 Pool company in the Nation for 13 years running.
          </p>
          <p className="mt-2 text-gray-600">
            &copy; Copyright 2026 American Pools &amp; Spas. All rights reserved. License # CPC1460475. Building services performed by DBPR licensed subcontractors.
          </p>
          <p className="mt-3 text-gray-600">
            Website Designed By{' '}
            <a
              href="https://bp.agency"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[#0b1e4d] hover:text-[#1e4fa3] hover:underline"
            >
              Brand Partners
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
